import apiClient from "../config/api";
import authService from "./authService";

const siteLayoutService = {
  /**
   * Get layout for a specific site
   * @param {number} siteId
   * @returns {Promise<object>}
   */
  getSiteLayout: async (siteId) => {
    try {
      const response = await apiClient.get(`/SiteLayout/plant/${siteId}`);
      return response.data;
    } catch (error) {
      console.error("Failed to fetch site layout:", error);
      return {
        level1: "",
        level2: "",
        level3: "",
        level4: "",
        level5: "",
        level6: "",
      };
    }
  },

  /**
   * Get global layout (applies to all sites if set)
   * @returns {Promise<object>}
   */
  getGlobalLayout: async () => {
    try {
      const response = await apiClient.get("/SiteLayout/global");
      return response.data;
    } catch (error) {
      console.error("Failed to fetch global layout:", error);
      return {
        level1: "",
        level2: "",
        level3: "",
        level4: "",
        level5: "",
        level6: "",
      };
    }
  },

  /**
   * Save layout for a specific site
   * @param {number} siteId
   * @param {object} layoutData
   * @param {boolean} applyToAll
   * @returns {Promise<object>}
   */
  saveSiteLayout: async (siteId, layoutData, applyToAll = false) => {
    try {
      const response = await apiClient.post("/SiteLayout", {
        plantId: siteId ? parseInt(siteId) : null,
        isGlobal: applyToAll,
        level1: layoutData.level1 || "",
        level2: layoutData.level2 || "",
        level3: layoutData.level3 || "",
        level4: layoutData.level4 || "",
        level5: layoutData.level5 || "",
        level6: layoutData.level6 || "",
        applyToAll: applyToAll,
      });
      return response.data;
    } catch (error) {
      console.error("Failed to save site layout:", error);
      throw error;
    }
  },

  // ---- Level Items Management ----

  /**
   * Get items for a specific level and plant
   * @param {number} siteId
   * @param {number} level
   * @returns {Promise<Array>}
   */
  getLevelItems: async (siteId, level) => {
    try {
      const response = await apiClient.get(
        `/SiteLayout/items/${siteId}/${level}`,
      );
      // Handle response wrapping
      if (Array.isArray(response.data)) return response.data;
      if (response.data && Array.isArray(response.data.data))
        return response.data.data;
      if (response.data && Array.isArray(response.data.items))
        return response.data.items;
      return [];
    } catch (error) {
      console.error("Failed to fetch level items:", error);
      return [];
    }
  },

  /**
   * Add a new level item
   * @param {object} item
   * @returns {Promise<object>}
   */
  addLevelItem: async (item) => {
    console.log("siteLayoutService.addLevelItem - Payload:", item);
    try {
      // Pass the payload directly since SiteLayout.js creates it correctly
      const response = await apiClient.post("/SiteLayout/items", item);
      return response.data;
    } catch (error) {
      console.error("Failed to add level item:", error);
      throw error;
    }
  },

  /**
   * Get a specific level item by ID
   * @param {number} itemId
   * @returns {Promise<object>}
   */
  getLevelItemById: async (itemId) => {
    try {
      const response = await apiClient.get(`/SiteLayout/items/${itemId}`);
      return response.data;
    } catch (error) {
      console.error("Failed to fetch level item:", error);
      return null;
    }
  },

  /**
   * Update an existing level item
   * @param {number} itemId
   * @param {object} updates
   * @returns {Promise<object>}
   */
  updateLevelItem: async (itemId, updates) => {
    try {
      const payload = {
        tenantId: updates.tenantId,
        plantId: updates.plantId,
        siteId: updates.plantId,
        level: updates.level,
        name: updates.name,
        head: updates.head || null,
        headUserId: updates.headUserId || null,
        parentId: updates.parentId || null,
        latitude: updates.latitude || null,
        longitude: updates.longitude || null,
        status: updates.status || "Active",
      };
      const response = await apiClient.post(
        `/SiteLayout/update/items/${itemId}`,
        payload,
      );
      return response.data;
    } catch (error) {
      console.error("Failed to update level item:", error);
      throw error;
    }
  },

  /**
   * Delete a level item (soft delete)
   * @param {number} itemId
   * @returns {Promise<boolean>}
   */
  deleteLevelItem: async (itemId) => {
    try {
      await apiClient.post(`/SiteLayout/delete/items/${itemId}`);
      return true;
    } catch (error) {
      console.error("Failed to delete level item:", error);
      throw error;
    }
  },

  /**
   * Search items for a specific level and plant
   * @param {number} siteId
   * @param {number} level
   * @param {string} searchTerm
   * @returns {Promise<Array>}
   */
  searchLevelItems: async (siteId, level, searchTerm) => {
    try {
      const response = await apiClient.get(
        `/SiteLayout/items/${siteId}/${level}/search`,
        { params: { q: searchTerm } },
      );
      if (Array.isArray(response.data)) return response.data;
      if (response.data && Array.isArray(response.data.data))
        return response.data.data;
      return [];
    } catch (error) {
      console.error("Failed to search level items:", error);
      return [];
    }
  },
};
export default siteLayoutService;
